









































import { computed, defineComponent, onMounted, Ref, ref, toRefs, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import {
  InputWelcomeMessage,
  InputWelcomeMessageFile,
  WelcomeMessage,
  WelcomeMessageFile,
  WelcomeMessageType,
} from "@scrile/api-provider/dist/api/WelcomeMessagesProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { getElement } from "@/lib/elementHelpers";
import { handleRequestError } from "@/lib/handleRequestError";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import { AttachmentFile } from "@/types";
import { ERRORS } from "@/const";
import BaseSwitch from "@/components/BaseSwitch/BaseSwitch.vue";
import MessageEditorView from "@/components/MessageEditorView";
import RestrictedWordConfirm from "@/components/RestrictedWordConfirm";
import { extractRestrictedWords } from "@/components/RestrictedWordConfirm/helpers";
import SendMessageController from "@/components/SendMessageController";

const DEFAULT_VISIBILITY_STATE = true;

const useWelcomeMessage = (subjectType: Ref<string>, subjectId?: Ref<string>) => {
  const { authUserStrict } = useAuthUser();
  const nodeRef = ref<HTMLDivElement>();
  const welcomeMessage = ref<WelcomeMessage | null>(null);
  const pricePackageId = ref("0");
  const isShowEditor = ref(DEFAULT_VISIBILITY_STATE);
  const value = ref("");
  const previewFiles = ref<AttachmentFile[]>([]);
  const showRestrictedWordConfirm = ref(false);
  const restrictedWords = ref<string[]>([]);

  const attachedFilesUpdate = (attachmentFiles: AttachmentFile[]) => {
    previewFiles.value = [...attachmentFiles];
  };

  const syncFilesWithVault = () => {
    if (!nodeRef.value) return;
    const element = getElement(nodeRef.value);
    const event = new Event("filesUpdated");
    element.dispatchEvent(event);
  };

  const updateAttachmentFiles = (cb: (files: AttachmentFile[]) => void) => cb(previewFiles.value);

  const isTextNotChanged = computed<boolean>(() => value.value.trim() === welcomeMessage.value?.text.trim());
  const isFilesNotChanged = computed<boolean>(() =>
    welcomeMessage.value
      ? previewFiles.value.every(p => !!welcomeMessage.value?.files.find(f => f.uuid === p.uid)) &&
        !!welcomeMessage.value?.files.every(f => !!previewFiles.value?.find(p => p.uid === f.uuid))
      : true
  );
  const isVisibilityNotChange = computed(() =>
    welcomeMessage.value ? isShowEditor.value === welcomeMessage.value?.enabled : true
  );
  const isPriceNotChange = computed(() =>
    welcomeMessage.value ? pricePackageId.value === welcomeMessage.value?.pricePackageId : true
  );
  const shapeFilesToSave = (attachmentFiles: AttachmentFile[]): InputWelcomeMessageFile[] =>
    attachmentFiles.map(f => ({
      uuid: f.uid,
      publicAccess: f.publicAccess,
    }));
  const shapeMessageDataToSave = (subjectId: string): InputWelcomeMessage[] => [
    {
      subject: { subjectId, subjectType: subjectType.value },
      text: value.value.trim(),
      enabled: isShowEditor.value,
      files: shapeFilesToSave(previewFiles.value),
      pricePackageId: pricePackageId.value,
    },
  ];
  const adapterToPreviewFiles = (files: WelcomeMessageFile[]): AttachmentFile[] => {
    return files.map(f => ({
      uid: f.uuid,
      type: f.metadata.contentType,
      src: getMediaThumbnailUrl(f, f.urlPart),
      publicAccess: f.publicAccess,
    }));
  };

  const findWelcomeMessageFromResponse = (messages: WelcomeMessage[]) =>
    messages.find(
      m => m.subject.subjectId === (subjectId?.value ?? "") && m.subject.subjectType === subjectType.value
    ) ?? null;

  const processing = ref(false);
  const saveWelcomeMessage = async (subjectId: string) => {
    if (!(isTextNotChanged.value && isFilesNotChanged.value && isVisibilityNotChange.value && isPriceNotChange.value)) {
      if (processing.value) return;
      try {
        processing.value = true;
        providers.WelcomeMessagesProvider.setReceiveErrors(false);
        const r = await providers.WelcomeMessagesProvider.update({
          userId: authUserStrict.value.id,
          messages: shapeMessageDataToSave(subjectId),
        });
        welcomeMessage.value = findWelcomeMessageFromResponse(r);
        return welcomeMessage.value;
      } catch (e) {
        let errText = "";
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === ERRORS.RESTRICTED_WORD,
            onSuccessCB: e => {
              restrictedWords.value = extractRestrictedWords(e);
              showRestrictedWordConfirm.value = true;
              errText = ERRORS.RESTRICTED_WORD;
            },
          },
        ]);
        return errText;
      } finally {
        processing.value = false;
      }
    }
  };
  const loading = ref(false);
  const getWelcomeMessageData = async () => {
    try {
      loading.value = true;
      const result = await providers.WelcomeMessagesProvider.findByUser({ userId: authUserStrict.value.id });
      welcomeMessage.value = findWelcomeMessageFromResponse(result);
      value.value = welcomeMessage.value?.text ?? "";
      previewFiles.value = adapterToPreviewFiles(welcomeMessage.value?.files ?? []);
      isShowEditor.value = welcomeMessage.value?.enabled ?? DEFAULT_VISIBILITY_STATE;
      pricePackageId.value = welcomeMessage.value?.pricePackageId ?? "0";
      syncFilesWithVault();
    } finally {
      loading.value = false;
    }
  };
  getWelcomeMessageData();
  return {
    value,
    nodeRef,
    previewFiles,
    isShowEditor,
    loading,
    processing,
    pricePackageId,
    showRestrictedWordConfirm,
    restrictedWords,
    updateAttachmentFiles,
    saveWelcomeMessage,
    attachedFilesUpdate,
  };
};

const ADMIN_ID = "1";

export default defineComponent({
  name: "WelcomeMessageEditor",
  components: {
    RestrictedWordConfirm,
    SendMessageController,
    MessageEditorView,
    BaseSwitch,
  },
  props: {
    switcherTitle: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    subjectId: { type: String, required: true },
    subjectType: {
      type: String as () => WelcomeMessage["subject"]["subjectType"],
      default: WelcomeMessageType.SUBSCRIBERS,
    },
  },
  setup(props, { emit }) {
    const { subjectId, subjectType } = toRefs(props);
    const {
      previewFiles,
      isShowEditor,
      nodeRef,
      value,
      loading,
      processing,
      pricePackageId,
      showRestrictedWordConfirm,
      restrictedWords,
      saveWelcomeMessage,
      updateAttachmentFiles,
      attachedFilesUpdate,
    } = useWelcomeMessage(subjectType, subjectId);

    const sendUpdateEvent = debounce(() => emit("updateWelcomeMessage"), 1000);

    onMounted(() => emit("saveWelcomeMessage", saveWelcomeMessage));

    watch([isShowEditor, previewFiles, pricePackageId], sendUpdateEvent);

    return {
      ADMIN_ID,
      value,
      nodeRef,
      previewFiles,
      isShowEditor,
      loading,
      processing,
      pricePackageId,
      showRestrictedWordConfirm,
      restrictedWords,
      updateAttachmentFiles,
      saveWelcomeMessage,
      attachedFilesUpdate,
      sendUpdateEvent,
    };
  },
});
